'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Building2,
  Check,
  Globe,
  Link as LinkIcon,
  Loader2,
  MapPin,
  NotebookPen,
  Search,
  Wand2,
} from 'lucide-react';

import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Textarea } from '@/components/ui/forms/textarea';
import { LocationAutosuggest } from '@/components/ui/forms/LocationAutosuggest';
import { Badge } from '@/components/ui/data-display/badge';
import {
  DialogHeading,
  FormSectionHeader,
  HintNote,
  SegmentedToggle,
} from '@/components/ui/design-system';
import { fetchJobs } from '@/actions/Jobs';
import { isSearchConfirmShortcut } from '@/lib/keyboard-shortcuts';
import {
  sanitizeJobContext,
  stringifyContextContent,
} from '@/lib/context-source-sanitizers';
import type { ExtractedJobFields, ExtractionStrategy } from '@/lib/jobs/extract-job-posting';
import type { Job } from '@/types/jobs-types';

/** Manual entry leads because it is the only mode that always works. */
type JobSourceMode = 'manual' | 'link' | 'search';

const MODE_OPTIONS = [
  { value: 'manual', label: 'Manual Entry', icon: NotebookPen },
  { value: 'link', label: 'By Link', icon: LinkIcon },
  { value: 'search', label: 'Search', icon: Search },
] as const satisfies ReadonlyArray<{ value: JobSourceMode; label: string; icon: typeof Search }>;

const EMPTY_JOB_FORM = {
  title: '',
  company: '',
  location: '',
  description: '',
};

type LinkFetchState =
  | { status: 'idle' }
  | { status: 'fetching' }
  | { status: 'extracted'; message: string }
  | { status: 'failed'; message: string };

interface ExtractResponse {
  ok?: boolean;
  fields?: ExtractedJobFields;
  strategy?: ExtractionStrategy;
  error?: string;
}

export interface RefineJobSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Header copy. Defaults to the whole-resume wording; the inline field controls
   * pass their own so the same dialog can refine one field without claiming it
   * is about to rewrite the entire document.
   */
  title?: string;
  description?: string;
  /** Confirm button label. Defaults to "Run refine". */
  confirmLabel?: string;
  /**
   * Persists the job source. Wire this straight to the page's existing
   * `handleSubmitSource`, which POSTs to
   * `/api/resume-builder/{resumeId}/sources`. Resolve `true` on success.
   */
  onAddSource: (payload: Record<string, unknown>) => Promise<boolean>;
  /** Runs the existing refine flow. Resolve `true` when refine succeeded. */
  onRefine: () => Promise<boolean>;
  /** True while the refine agent is running; drives the confirm button state. */
  isRefining?: boolean;
  /**
   * Slot for the caller's `<ResumeRefineProgress />`. Kept as a slot so the
   * refine agent's stream stays owned by the page, not by this dialog.
   */
  progressSlot?: React.ReactNode;
}

/**
 * Single-purpose Refine dialog: one job source, nothing else.
 *
 * The payloads handed to `onAddSource` are byte-for-byte what the shared
 * `AddSourceDialog` sends for a JOB source, so persistence is unchanged — this
 * is a UI simplification, not a data-model change.
 */
export function RefineJobSourceDialog({
  open,
  onOpenChange,
  onAddSource,
  onRefine,
  isRefining = false,
  progressSlot,
  title = 'Refine Resume',
  description = 'Refine rewrites your resume against a target job. Add that job below, then run refine.',
  confirmLabel = 'Run refine',
}: RefineJobSourceDialogProps) {
  const [mode, setMode] = useState<JobSourceMode>('manual');
  const [jobForm, setJobForm] = useState(EMPTY_JOB_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // By Link
  const [linkUrl, setLinkUrl] = useState('');
  const [linkFetch, setLinkFetch] = useState<LinkFetchState>({ status: 'idle' });

  // Search
  const [searchForm, setSearchForm] = useState({ title: '', location: '', company: '' });
  const [searchResults, setSearchResults] = useState<Job[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const resultsAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) return;
    // Reset on close so a reopened dialog never shows a stale extraction.
    setMode('manual');
    setJobForm(EMPTY_JOB_FORM);
    setFormError(null);
    setLinkUrl('');
    setLinkFetch({ status: 'idle' });
    setSearchForm({ title: '', location: '', company: '' });
    setSearchResults([]);
    setSearchError(null);
    setSelectedJob(null);
  }, [open]);

  const handleModeChange = (nextMode: JobSourceMode) => {
    setMode(nextMode);
    setFormError(null);
  };

  const handleFetchFromLink = useCallback(async () => {
    const url = linkUrl.trim();
    if (!url) {
      setLinkFetch({ status: 'failed', message: 'Enter a job posting URL first.' });
      return;
    }

    setFormError(null);
    setLinkFetch({ status: 'fetching' });

    try {
      const response = await fetch('/api/jobs/extract-from-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = (await response.json().catch(() => null)) as ExtractResponse | null;

      if (!response.ok || !data?.ok) {
        setLinkFetch({
          status: 'failed',
          message:
            data?.error ??
            "We couldn't pull the details from that link. Add them below and refine anyway.",
        });
        return;
      }

      const fields = data.fields ?? {};
      const foundAnything = Boolean(
        fields.title || fields.company || fields.location || fields.description
      );

      if (!foundAnything) {
        setLinkFetch({
          status: 'failed',
          message:
            "We reached the page but couldn't find job details on it. Add them below and refine anyway.",
        });
        return;
      }

      setJobForm({
        title: fields.title ?? '',
        company: fields.company ?? '',
        location: fields.location ?? '',
        description: fields.description ?? '',
      });
      setLinkFetch({
        status: 'extracted',
        message: 'Pulled from the link. Check the details below — extraction is a starting point.',
      });
    } catch {
      setLinkFetch({
        status: 'failed',
        message:
          "We couldn't pull the details from that link. Add them below and refine anyway.",
      });
    }
  }, [linkUrl]);

  const handleSearch = useCallback(async () => {
    if (!searchForm.title.trim() && !searchForm.location.trim()) {
      setSearchError('Enter a job title or location.');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSelectedJob(null);

    try {
      const response = await fetchJobs({
        title: searchForm.title.trim() || undefined,
        location: searchForm.location.trim() || undefined,
        companies: searchForm.company.trim() ? [searchForm.company.trim()] : undefined,
        page: 1,
        limit: 10,
        mode: 'expand_keywords_internal_vector_paid_shuffle_organic',
      });
      const nextResults = response.results || [];
      setSearchResults(nextResults);
      if (nextResults.length > 0) {
        requestAnimationFrame(() => {
          resultsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Job search failed.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchForm]);

  const handleSearchFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (!isSearchConfirmShortcut(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.requestSubmit();
  };

  const buildPayload = (): Record<string, unknown> | null => {
    if (mode === 'search') {
      if (!selectedJob) {
        setFormError('Select a job from the search results.');
        return null;
      }
      const contentJson =
        sanitizeJobContext(selectedJob, {
          id: selectedJob.job_id || selectedJob.id,
          title: selectedJob.title,
          company: selectedJob.company,
          location:
            typeof selectedJob.location === 'string'
              ? selectedJob.location
              : [selectedJob.location?.city, selectedJob.location?.state]
                  .filter(Boolean)
                  .join(', ') || undefined,
          description: selectedJob.description || undefined,
        }) || {};
      return {
        type: 'JOB',
        title: selectedJob.title,
        description: selectedJob.company,
        contentText: stringifyContextContent(contentJson),
        contentJson,
      };
    }

    if (!jobForm.title.trim() || !jobForm.company.trim()) {
      setFormError('Enter a job title and company.');
      return null;
    }

    const contentJson =
      sanitizeJobContext(
        {},
        {
          title: jobForm.title.trim(),
          company: jobForm.company.trim(),
          location: jobForm.location.trim() || undefined,
          description: jobForm.description.trim() || undefined,
        }
      ) || {};

    return {
      type: 'JOB',
      title: jobForm.title.trim(),
      description: jobForm.company.trim(),
      contentText: stringifyContextContent(contentJson),
      contentJson,
    };
  };

  const handleConfirm = async () => {
    setFormError(null);

    const payload = buildPayload();
    if (!payload) return;

    setIsSaving(true);
    let saved = false;
    try {
      saved = await onAddSource(payload);
    } finally {
      setIsSaving(false);
    }

    if (!saved) {
      setFormError('Unable to save the job source. Please try again.');
      return;
    }

    const refined = await onRefine();
    if (refined) {
      onOpenChange(false);
    }
  };

  const isBusy = isSaving || isRefining;
  const showLinkFields = linkFetch.status === 'extracted' || linkFetch.status === 'failed';

  const jobDetailFields = (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-label">Job Title *</Label>
          <Input
            placeholder="Senior Software Engineer"
            value={jobForm.title}
            onChange={(event) => setJobForm({ ...jobForm, title: event.target.value })}
            className="mt-1 border"
          />
        </div>
        <div>
          <Label className="text-label">Company *</Label>
          <Input
            placeholder="Acme Inc."
            value={jobForm.company}
            onChange={(event) => setJobForm({ ...jobForm, company: event.target.value })}
            className="mt-1 border"
          />
        </div>
      </div>
      <div>
        <Label className="text-label">Location (optional)</Label>
        <Input
          placeholder="e.g., Remote, San Francisco"
          value={jobForm.location}
          onChange={(event) => setJobForm({ ...jobForm, location: event.target.value })}
          className="mt-1 border"
        />
      </div>
      <div>
        <Label className="text-label">Job Description (optional)</Label>
        <Textarea
          placeholder="Paste the full job description..."
          value={jobForm.description}
          onChange={(event) => setJobForm({ ...jobForm, description: event.target.value })}
          rows={5}
          className="mt-1 border"
        />
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent layout="panel" className="min-[640px]:max-w-2xl">
        <DialogHeading
          leading={
            <div className="flex size-9 items-center justify-center border border-border/60 bg-muted/30 text-primary">
              <Wand2 className="size-4" />
            </div>
          }
          title={title}
          description={description}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-4 px-4 py-4 min-[640px]:px-6">
            <div className="space-y-4 min-[640px]:rounded-sm min-[640px]:border min-[640px]:border-border min-[640px]:bg-card min-[640px]:p-5">
              <FormSectionHeader
                title="Job source"
                description="Search a posting, use a link, or add job details manually."
              />

              <SegmentedToggle
                value={mode}
                onChange={handleModeChange}
                options={MODE_OPTIONS}
                fullWidth
                ariaLabel="Job source mode"
                className="mb-4"
              />

              {mode === 'manual' && <div className="space-y-3">{jobDetailFields}</div>}

              {mode === 'link' && (
                <div className="space-y-3">
                  <div>
                    <Label className="flex items-center gap-1 text-label">
                      <Globe className="size-3" />
                      Job Posting URL
                    </Label>
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                      <Input
                        placeholder="https://company.com/careers/job-posting"
                        value={linkUrl}
                        onChange={(event) => setLinkUrl(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          void handleFetchFromLink();
                        }}
                        className="border"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void handleFetchFromLink()}
                        disabled={linkFetch.status === 'fetching'}
                        className="border sm:w-32 sm:shrink-0"
                      >
                        {linkFetch.status === 'fetching' ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <LinkIcon className="size-4" />
                        )}
                        {linkFetch.status === 'fetching' ? 'Fetching' : 'Fetch'}
                      </Button>
                    </div>
                    <p className="mt-1.5 text-caption text-muted-foreground">
                      We try to read the posting and fill the fields for you. You can always edit
                      them.
                    </p>
                  </div>

                  {linkFetch.status === 'extracted' && (
                    <HintNote tone="success" icon={<Check className="size-4" />}>
                      {linkFetch.message}
                    </HintNote>
                  )}
                  {linkFetch.status === 'failed' && (
                    <HintNote className="border-amber-300 bg-amber-50/80 text-amber-900">
                      {linkFetch.message}
                    </HintNote>
                  )}

                  {showLinkFields && jobDetailFields}
                </div>
              )}

              {mode === 'search' && (
                <div className="space-y-4">
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleSearch();
                    }}
                    onKeyDown={handleSearchFormKeyDown}
                    className="space-y-3 rounded-lg border border-border bg-muted/30 p-4"
                  >
                    <div>
                      <Label className="text-label">Job Title</Label>
                      <Input
                        placeholder="e.g., Software Engineer, Product Manager"
                        value={searchForm.title}
                        onChange={(event) =>
                          setSearchForm({ ...searchForm, title: event.target.value })
                        }
                        className="mt-1.5 border"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-label">Location</Label>
                        <div className="mt-1.5">
                          <LocationAutosuggest
                            value={searchForm.location}
                            onChange={(value) => setSearchForm({ ...searchForm, location: value })}
                            placeholder="City, state, or 'Remote'"
                            inputClassName="border"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-label">Company</Label>
                        <Input
                          placeholder="e.g., Google, Microsoft"
                          value={searchForm.company}
                          onChange={(event) =>
                            setSearchForm({ ...searchForm, company: event.target.value })
                          }
                          className="mt-1.5 border"
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={isSearching} className="w-full border">
                      {isSearching ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Search className="size-4" />
                      )}
                      {isSearching ? 'Searching' : 'Search Jobs'}
                    </Button>
                  </form>

                  {searchError && (
                    <HintNote className="border-destructive/40 bg-destructive/10 text-destructive">
                      {searchError}
                    </HintNote>
                  )}

                  {searchResults.length > 0 && (
                    <div className="space-y-3">
                      <div ref={resultsAnchorRef} className="flex items-center justify-between">
                        <Label className="text-label">
                          Select a job ({searchResults.length} found)
                        </Label>
                        {selectedJob && (
                          <Badge variant="secondary" className="text-caption">
                            Selected
                          </Badge>
                        )}
                      </div>
                      <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                        {searchResults.map((job) => {
                          const isSelected =
                            selectedJob !== null &&
                            Boolean(
                              (selectedJob.job_id &&
                                job.job_id &&
                                selectedJob.job_id === job.job_id) ||
                                (selectedJob.id && job.id && selectedJob.id === job.id)
                            );
                          return (
                            <button
                              key={job.job_id || job.id}
                              type="button"
                              onClick={() => setSelectedJob(isSelected ? null : job)}
                              className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all ${
                                isSelected
                                  ? 'border-[var(--burgundy-core)] bg-[var(--burgundy-core)]'
                                  : 'border-muted bg-card hover:border-border'
                              }`}
                            >
                              {isSelected && (
                                <div className="flex size-8 shrink-0 items-center justify-center bg-[color-mix(in_srgb,var(--burgundy-core),black_25%)]">
                                  <Check className="size-4 text-white" strokeWidth={3} />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div
                                  className={`text-label ${isSelected ? 'text-white' : 'text-foreground'}`}
                                >
                                  {job.title}
                                </div>
                                <div
                                  className={`mt-1 flex items-center gap-1 text-caption ${
                                    isSelected ? 'text-white/70' : 'text-muted-foreground'
                                  }`}
                                >
                                  <Building2 className="size-3" />
                                  {job.company}
                                </div>
                                {(job.location || job.city) && (
                                  <div
                                    className={`mt-1 flex items-center gap-1 text-caption ${
                                      isSelected ? 'text-white/70' : 'text-muted-foreground'
                                    }`}
                                  >
                                    <MapPin className="size-3" />
                                    {typeof job.location === 'string'
                                      ? job.location
                                      : [
                                          job.location?.city || job.city,
                                          job.location?.state || job.state,
                                        ]
                                          .filter(Boolean)
                                          .join(', ')}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {progressSlot}
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-border bg-background px-4 py-4 min-[640px]:px-6">
          <div className="flex w-full flex-col gap-3">
            {formError && (
              <HintNote className="border-destructive/40 bg-destructive/10 text-destructive">
                {formError}
              </HintNote>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
                className="border"
              >
                Cancel
              </Button>
              <Button onClick={() => void handleConfirm()} disabled={isBusy} className="border">
                {isBusy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Wand2 className="size-4" />
                )}
                {isSaving ? 'Saving' : isRefining ? 'Refining' : confirmLabel}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
